'use client';

import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { ArrowLeft } from '@ironhack/design-system2/icons/arrow-left';
import { useRouter } from 'next/navigation';
import { type ReactElement } from 'react';
import { type SetRequired } from 'type-fest';

import { useIsClient } from '@/app/components/client/client-only';
import { Link } from '@/app/components/client/link';
import { type BasePageParams, getPageUrl } from '@/app/urls/url-mapping';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { type DatoTargetBlock } from '@/lib/datocms/types';

const BackButton = (props: FlexProps): ReactElement => {
  const { children, ...flexProps } = props;
  const router = useRouter();

  return (
    <Flex as="button" onClick={() => router.back()} {...flexProps}>
      {children}
    </Flex>
  );
};

const getDefaultTarget = (params: BasePageParams): DatoTargetBlock | null => {
  const path = getPageUrl('home', params);
  return path
    ? {
        route: { path },
      }
    : null;
};

type BackLinkProps = {
  target?: DatoTargetBlock | null;
} & FlexProps;

const BackLink = (props: BackLinkProps): ReactElement => {
  const { children, target, ...flexProps } = props;
  const { color } = flexProps;

  const { params } = usePageContext();
  const backTarget = target || getDefaultTarget(params);

  return (
    <Flex _hover={{ color }} as={Link} {...flexProps} {...backTarget}>
      {children}
    </Flex>
  );
};

type Props = {
  target?: DatoTargetBlock | null;
  text?: string;
  // Set color required since becoming a link will change it unintentionally
} & SetRequired<FlexProps, 'color'>;

export const NavbarBack = (props: Props): ReactElement => {
  const { target, text, ...flexProps } = props;
  const isClient = useIsClient();
  const isBackButton = isClient && window.history?.length > 1 && !target;

  return isBackButton ? (
    <BackButton align="center" gap={1} {...flexProps}>
      <ArrowLeft />
      {text && (
        <Text as="span" textStyle="l">
          {text}
        </Text>
      )}
    </BackButton>
  ) : (
    <BackLink align="center" gap={1} target={target} {...flexProps}>
      <ArrowLeft />
      {text && (
        <Text as="span" textStyle="l">
          {text}
        </Text>
      )}
    </BackLink>
  );
};
